import React from 'react';
import { useTranslation } from 'react-i18next';
import Image from 'src/components/ui/Image/Image';
import Icon from 'src/components/ui/Icon/Icon';
import { Link } from 'react-router-dom';
import { formatConnectorHost } from 'src/utils/general';
import styles from '../../../pages/styles/Integrations.module.scss';
import Badge, { BadgeType } from '../../ui/Badge/Badge';
import Toggle from '../../ui/Toggle/Toggle';
import Button, { ButtonToneProps } from '../../ui/Button/Button';

interface IntegrationProps {
  id: number;
  image: string;
  title: string;
  titleUrl?: string;
  // Shop host/URL, shown under the title so multiple connectors of the same
  // channel type (e.g. two Shopify shops) can be told apart.
  host?: string | null;
  description: string;
  active: boolean;
  onDeactivate: (id: number) => void;
  onActivate: (id: number) => void;
  allowEdition?: boolean;
  // Link-card variant: when set, the card is a simple link (e.g. API docs) —
  // the button opens the URL in a new tab, no activate/toggle behaviour.
  externalUrl?: string;
  buttonLabel?: string;
}

const IntegrationCard: React.FC<IntegrationProps> = ({
  id,
  image,
  title,
  host,
  description,
  active,
  onActivate,
  onDeactivate,
  titleUrl,
  allowEdition = true,
  externalUrl,
  buttonLabel,
}) => {
  const { t } = useTranslation();
  const displayHost = formatConnectorHost(host);
  return (
    <div className={styles.integrationCard}>
      <div className="flex flex-col p-20 border-subtle-bottom flex-1">
        <div className="flex justify-between mb-16">
          <Image
            imageUrl={image}
            alt={title}
            className="w-16 h-16"
            fallbackComponent={<Icon name="package" width={20} height={20} />}
          />
        </div>
        <div className="flex flex-col gap-12 mb-20">
          <div className="flex flex-col gap-6 min-w-0">
            {titleUrl ? (
              <Link
                to={titleUrl}
                className="typo-title-20 2xl:typo-title-24 break-spaces text-primary-blue hover:text-primary-blue-hover"
                title={title}>
                {title}
              </Link>
            ) : (
              <h3 className="typo-title-20 2xl:typo-title-24 break-all">{title}</h3>
            )}
            {displayHost && (
              <span className="typo-text-12 text-faded truncate" title={host ?? undefined}>
                {displayHost}
              </span>
            )}
          </div>

          {active && <Badge expanded label={t('integrations.active')} showDot dotFilled type={BadgeType.Successful} />}
        </div>
        <p title={description} className="typo-text-12 text-faded text-ellipsis line-clamp-2">
          {description}
        </p>
      </div>
      <div className="p-20 flex justify-center items-center">
        {externalUrl ? (
          <Button
            className="w-full"
            onClick={() => window.open(externalUrl, '_blank', 'noopener,noreferrer')}
            tone={ButtonToneProps.PRIMARY_BLUE}
            title={buttonLabel ?? t('integrations.open_api_docs')}
          />
        ) : (
          <>
            {active && (
              <div className="mr-20">
                <Toggle disabled={!allowEdition} checked={active} onToggle={() => onDeactivate(id)} />
              </div>
            )}
            <Button
              disabled={active || !allowEdition}
              className="w-full"
              onClick={() => onActivate(id)}
              tone={ButtonToneProps.PRIMARY_BLUE}
              title={t('integrations.add_and_activate')}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default IntegrationCard;
